import {
  ApproveAiDetectionEventProperties,
  RejectAiDetectionEventProperties,
} from "@/analytics/ai-detection-events-properties";
import { EventType } from "@/analytics/analytics-events";
import { FaroAiChip } from "@/components/common/ai-assets/ai-chip";
import { useApproveRejectAiMarkup } from "@/hooks/use-ai-markup-approve-reject";
import { useReviewAnnotationInScene } from "@/hooks/use-review-annotation-in-scene";
import { selectCurrentAnnotation } from "@/store/ai-asset-review/ai-asset-review-selectors";
import { AiAssetReviewStatus } from "@/store/ai-asset-review/ai-asset-review-slice";
import { useAppSelector } from "@/store/store-hooks";
import {
  CircularProgress,
  CloseIcon,
  CompassGoToIcon,
  FaroIconButton,
  FaroText,
  FontWeights,
  grey,
  NO_TRANSLATE_CLASS,
  PointCloudIcon,
  TextField,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useState, type JSX } from "react";
import { AiAnnotationStatusDisplay } from "./ai-review-components/ai-annotation-status-display";

/** @returns step-by-step view component for reviewing individual annotations */
export function AiReviewStepByStepView(): JSX.Element {
  const currentAnnotation = useAppSelector(selectCurrentAnnotation);

  const reviewAnnotationInScene = useReviewAnnotationInScene(
    currentAnnotation?.markup,
  );
  const [reviewNotes, setReviewNotes] = useState("");
  const handleReviewNotesChange = useCallback((value: string) => {
    setReviewNotes(value);
  }, []);

  useEffect(() => {
    setReviewNotes(currentAnnotation?.reviewNotes ?? "");
  }, [currentAnnotation?.id, currentAnnotation?.reviewNotes]);

  const { approve, reject, isRunning } = useApproveRejectAiMarkup();
  const handleApprove = useCallback(async () => {
    if (!currentAnnotation) return;
    const approvedLabel = await approve(
      currentAnnotation.markup,
      reviewNotes || undefined,
    );
    if (approvedLabel) {
      Analytics.track<ApproveAiDetectionEventProperties>(
        EventType.approveAiDetection,
        {
          assetType: approvedLabel.name,
          hasReviewNotes: !!(reviewNotes || undefined)?.trim(),
          via: "step-by-step",
        },
      );
    }
  }, [approve, currentAnnotation, reviewNotes]);

  const handleReject = useCallback(async () => {
    if (!currentAnnotation) return;
    const rejectedLabel = await reject(
      currentAnnotation.markup,
      reviewNotes || undefined,
    );
    if (rejectedLabel) {
      Analytics.track<RejectAiDetectionEventProperties>(
        EventType.rejectAiDetection,
        {
          assetType: rejectedLabel.name,
          hasReviewNotes: !!(reviewNotes || undefined)?.trim(),
          via: "step-by-step",
        },
      );
    }
  }, [reject, currentAnnotation, reviewNotes]);

  const handleGoTo = useCallback(() => {
    if (!currentAnnotation) return;
    reviewAnnotationInScene(currentAnnotation.id);
  }, [reviewAnnotationInScene, currentAnnotation]);

  const isAlreadyReviewed =
    currentAnnotation?.status !== AiAssetReviewStatus.pending;

  if (!currentAnnotation) {
    return (
      <Box
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <FaroText variant="bodyM" color={grey[600]}>
          No annotation selected
        </FaroText>
      </Box>
    );
  }

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        height: "fit-content",
        border: 0.5,
        borderColor: grey[200],
      }}
    >
      <Stack sx={{ p: 3, minWidth: 0 }} spacing={3}>
        {/* Task Name Header */}
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <FaroText
            variant="labelM"
            color={grey[900]}
            sx={{ fontWeight: FontWeights.Bold }}
            className={NO_TRANSLATE_CLASS}
          >
            {currentAnnotation.title}
          </FaroText>
          <FaroIconButton
            aria-label="go to annotation"
            color="inherit"
            size="s"
            onClick={handleGoTo}
            margin={0}
          >
            <CompassGoToIcon />
          </FaroIconButton>
        </Stack>

        {/* Identified Asset Section */}
        <Stack spacing={1}>
          <FaroText
            variant="bodyS"
            sx={{ fontWeight: FontWeights.Bold, color: grey[600] }}
          >
            Identified asset
          </FaroText>

          {/* Asset Preview Area */}
          <Box
            component="div"
            sx={{
              width: "100%",
              height: 300,
              backgroundColor: grey[100],
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: 1,
              borderColor: grey[200],
            }}
          >
            {currentAnnotation.thumbnail && (
              <img
                src={currentAnnotation.thumbnail}
                alt="AI detected asset preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </Stack>

        {/* AI Generated Tag and Extracted From - Side by Side */}
        <Stack
          direction="row"
          sx={{ flexWrap: "wrap", columnGap: "7.5rem", rowGap: "1.5rem" }}
        >
          {/* AI Generated Tag Section */}
          <Stack spacing={1} sx={{ minWidth: "8rem" }}>
            <FaroText
              variant="bodyS"
              sx={{ fontWeight: FontWeights.Bold, color: grey[600] }}
            >
              AI generated tag
            </FaroText>
            <FaroAiChip label={currentAnnotation.tag.toUpperCase()} />
          </Stack>

          {/* Extracted From Section */}
          <Stack spacing={1} sx={{ minWidth: "10rem" }}>
            <FaroText
              variant="bodyS"
              sx={{ fontWeight: FontWeights.Bold, color: grey[600] }}
            >
              Extracted from
            </FaroText>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <PointCloudIcon
                sx={{ width: 16, height: 16, color: "primary.darker" }}
              />
              <FaroText
                variant="bodyS"
                color={grey[600]}
                className={NO_TRANSLATE_CLASS}
              >
                Point cloud
              </FaroText>
              <FaroText
                variant="bodyS"
                color={grey[600]}
                className={NO_TRANSLATE_CLASS}
              >
                {currentAnnotation.area}
              </FaroText>
            </Stack>
          </Stack>
        </Stack>

        {/* Review Notes Section */}
        <Stack spacing={1} sx={{ width: "100%", minWidth: 0 }}>
          <FaroText
            variant="bodyS"
            sx={{ fontWeight: FontWeights.Bold, color: grey[600] }}
          >
            Review notes (optional)
          </FaroText>
          <TextField
            multiline
            rows={3}
            placeholder="Add any notes about this annotation"
            text={reviewNotes}
            onTextChanged={handleReviewNotesChange}
            disabled={isAlreadyReviewed || isRunning}
            fullWidth
            controlStyle={{ minWidth: 0 }}
            sx={{
              minWidth: 0,
              "& .MuiInputBase-input::placeholder": {
                whiteSpace: "normal",
              },
              "& textarea::placeholder": {
                whiteSpace: "normal",
              },
            }}
          />
        </Stack>

        {/* Loading Indicator */}
        {isRunning && (
          <Box
            component="div"
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <CircularProgress size={20} />
          </Box>
        )}

        {/* Action Buttons or Status */}
        {isAlreadyReviewed && !isRunning && (
          <AiAnnotationStatusDisplay status={currentAnnotation.status} />
        )}
        {!isAlreadyReviewed && !isRunning && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <FaroIconButton
              color="error"
              onClick={handleReject}
              disabled={isRunning}
              aria-label="Reject annotation"
              size="m"
            >
              <CloseIcon />
            </FaroIconButton>
            <FaroIconButton
              color="success"
              onClick={handleApprove}
              disabled={isRunning}
              aria-label="Approve annotation"
              size="m"
            >
              <CheckIcon />
            </FaroIconButton>
          </Stack>
        )}

        {/* Warning Text */}
        {!isAlreadyReviewed && (
          <FaroText
            variant="bodyXS"
            color={grey[600]}
            sx={{ textAlign: "center", display: "block" }}
          >
            The action cannot be undone
          </FaroText>
        )}
      </Stack>
    </Box>
  );
}
